// assets/product-gallery-swiper.js
class ProductGallerySwiper extends HTMLElement {
  constructor() {
    super();

    this._bound = false;

    this.main = null;
    this.thumbs = null;
    this.lightbox = null;

    this.onResize = this.onResize.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
  }

  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    queueMicrotask(() => {
      this.cacheEls();
      this.initSwiper();
      this.bindModelViewerHoverLock();
      this.bindVariantButtonsDelegation();
      this.bindVariantChangeEvent();

      this.initZoomDrift();      // ✅ normal + magnifier + inner
      this.initPhotoSwipe();
      this.initModelViewerUI();

      this.syncAllFromActiveSlide();
    });

    window.addEventListener("resize", this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("click", this.onDocClick);
    this.removeEventListener("variant:change", this.onVariantChange);

    this.destroyPhotoSwipe();
    this.destroyZoomDrift();
    this.destroySwiper();

    this._bound = false;
  }

  // -----------------------
  // Getters / helpers
  // -----------------------
  get galleryId() {
    return this.dataset.galleryId || "gallery-swiper-started";
  }

  get thumbsDirection() {
    // wrapper attr ưu tiên, nếu không có thì lấy từ thumbs element
    return (
      this.dataset.thumbsDirection ||
      this.querySelector(".tf-product-media-thumbs")?.dataset?.direction ||
      "vertical"
    );
  }

  get thumbsPreview() {
    const fromWrapper = parseInt(this.dataset.thumbsPreview || "0", 10);
    if (fromWrapper) return fromWrapper;

    const fromThumb = parseInt(this.querySelector(".tf-product-media-thumbs")?.dataset?.preview || "0", 10);
    return fromThumb || 4;
  }

  cap(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  getScopeEl() {
    // ưu tiên scope trong section Shopify để tránh ăn sang product khác
    return this.closest(".shopify-section") || document;
  }

  cacheEls() {
    this.mainEl = this.querySelector(".tf-product-media-main");
    this.thumbsEl = this.querySelector(".tf-product-media-thumbs");
  }

  // -----------------------
  // Swiper
  // -----------------------
  initSwiper() {
    if (typeof Swiper === "undefined") return;
    if (!this.mainEl || !this.thumbsEl) return;

    this.thumbs = new Swiper(this.thumbsEl, {
      spaceBetween: 15,
      slidesPerView: this.thumbsPreview,
      freeMode: true,
      direction: this.thumbsDirection,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      breakpoints: {
        0: {
          direction: "horizontal",
          slidesPerView: this.thumbsPreview,
        },
        1200: {
          direction: this.thumbsDirection,
        },
      },
    });

    this.main = new Swiper(this.mainEl, {
      spaceBetween: 0,
      observer: true,
      observeParents: true,
      speed: 800,
      loop:true,
      navigation: {
        nextEl: ".thumbs-next",
        prevEl: ".thumbs-prev",
      },
      pagination: {
        el: ".thumbs-pagination",
        type: "fraction",
        renderFraction: function (prev, next) {
          return `<span class="${prev}"></span><span class="swiper-slice"></span> <span class="${next}"></span>`;
        },
      },
      thumbs: {
        swiper: this.thumbs && this.thumbs.slides?.length ? this.thumbs : null,
      },
    });

    this.main.on("slideChange", () => {
      this.syncAllFromActiveSlide();
    });
  }

  destroySwiper() {
    try {
      this.main?.destroy(true, true);
      this.thumbs?.destroy(true, true);
    } catch (_) {}
    this.main = null;
    this.thumbs = null;
  }

  // -----------------------
  // Lock swipe on 3D hover
  // -----------------------
  bindModelViewerHoverLock() {
    if (!this.main) return;

    const slide3d = this.querySelector(".slide-3d");
    if (!slide3d) return;

    slide3d.addEventListener("mouseenter", () => (this.main.allowTouchMove = false));
    slide3d.addEventListener("mouseleave", () => (this.main.allowTouchMove = true));
  }

  // -----------------------
  // Variant buttons (NO jQuery)
  // -----------------------
  bindVariantButtonsDelegation() {
    document.addEventListener("click", this.onDocClick);
  }

  onDocClick(e) {
    const btn = e.target.closest(".color-btn, .size-btn");
    if (!btn) return;

    const scope = this.getScopeEl();
    if (scope !== document && !scope.contains(btn)) return;
    if (btn.closest(".modal-quick-view")) return;

    const type = btn.classList.contains("size-btn") ? "size" : "color";
    const value = btn.getAttribute(`data-${type}`) || btn.dataset?.[type] || "";
    if (!value) return;

    // active state
    scope.querySelectorAll(`.${type}-btn`).forEach((b) => b.classList.remove("active"));
    btn.classList.add("active");

    const currentColor =
      scope.querySelector(".tf-product-info-list .value-currentColor")?.textContent?.trim() || "";

    this.scrollToMatchingSlide(type, value, currentColor);
  }

  syncAllFromActiveSlide() {
    if (!this.main) return;
    const idx = this.main.activeIndex;

    this.updateActiveButtonFromSlide("color", idx);
    this.updateActiveButtonFromSlide("size", idx);
  }

  updateActiveButtonFromSlide(type, activeIndex) {
    const scope = this.getScopeEl();
    const slides = this.querySelectorAll(".tf-product-media-main .swiper-slide");
    const currentSlide = slides[activeIndex];
    if (!currentSlide) return;

    const value = currentSlide.getAttribute(`data-${type}`);
    if (!value) return;

    // buttons
    scope.querySelectorAll(`.${type}-btn`).forEach((b) => b.classList.remove("active"));
    scope
      .querySelectorAll(`.${type}-btn[data-${type}="${CSS.escape(value)}"]`)
      .forEach((b) => b.classList.add("active"));

    // texts
    const currentEl = scope.querySelector(`.tf-product-info-list .value-current${this.cap(type)}`);
    const selectEl = scope.querySelector(`.tf-product-info-list .select-current${this.cap(type)}`);
    if (currentEl) currentEl.textContent = value;
    if (selectEl) selectEl.textContent = value;
  }

  scrollToMatchingSlide(type, value, color) {
    if (!this.main || !value) return;

    const slides = Array.from(this.querySelectorAll(".tf-product-media-main .swiper-slide"));

    let idx = slides.findIndex(
      (s) => s.getAttribute(`data-${type}`) === value && s.getAttribute("data-color") === color
    );
    if (idx < 0) idx = slides.findIndex((s) => s.getAttribute(`data-${type}`) === value);

    if (idx >= 0) {
      this.main.slideTo(idx, 1000, false);
      if (this.thumbs?.slides?.length) this.thumbs.slideTo(idx, 1000, false);
    }
  }

  // -----------------------
  // Variant change hook (optional)
  // -----------------------
  bindVariantChangeEvent() {
    this.addEventListener("variant:change", this.onVariantChange);
  }

  onVariantChange(e) {
    const color = e?.detail?.color;
    const size = e?.detail?.size;

    if (size && color) this.scrollToMatchingSlide("size", String(size), String(color));
    else if (color) this.scrollToMatchingSlide("color", String(color), String(color));
  }

  // -----------------------
  // Zoom (Drift) - FULL (no jQuery)
  // -----------------------
  initZoomDrift() {
    // hover class (chỉ áp cho .tf-image-zoom)
    this.querySelectorAll(".tf-image-zoom").forEach((img) => {
      img.addEventListener("mouseover", () =>
        img.closest(".section-image-zoom")?.classList.add("zoom-active")
      );
      img.addEventListener("mouseleave", () =>
        img.closest(".section-image-zoom")?.classList.remove("zoom-active")
      );
    });

    this.applyAllDrifts();
  }

  onResize() {
    // re-evaluate desktop/mobile for normal zoom
    this.applyAllDrifts();
  }

  applyAllDrifts() {
    if (typeof Drift === "undefined") return;

    const isDesktop = matchMedia("only screen and (min-width: 1200px)").matches;

    // Mobile/tablet: bỏ toàn bộ zoom
    if (!isDesktop) {
      this.destroyZoomDrift();
      return;
    }

    const paneMain =
      this.querySelector(".tf-zoom-main") || document.querySelector(".tf-zoom-main");

    this.querySelectorAll(".tf-image-zoom").forEach((el) => {
      if (!el._drift) {
        el._drift = new Drift(el, {
          zoomFactor: 2,
          paneContainer: paneMain || undefined,
          inlinePane: false,
          handleTouch: false,
          hoverBoundingBox: true,
          containInline: true,
        });
      }
    });

    this.querySelectorAll(".tf-image-zoom-magnifier").forEach((el) => {
      if (!el._drift) {
        el._drift = new Drift(el, {
          zoomFactor: 2,
          inlinePane: true,
          containInline: false,
        });
      }
    });

    this.querySelectorAll(".tf-image-zoom-inner").forEach((el) => {
      if (!el._drift) {
        el._drift = new Drift(el, {
          paneContainer: el.closest(".tf-product-zoom-inner"),
          zoomFactor: 2,
          inlinePane: false,
          containInline: false,
        });
      }
    });
  }

  destroyZoomDrift() {
    const all = this.querySelectorAll(
      ".tf-image-zoom, .tf-image-zoom-magnifier, .tf-image-zoom-inner"
    );
    all.forEach((el) => {
      if (el._drift) {
        try { el._drift.destroy(); } catch (_) {}
        el._drift = null;
      }
    });
  }

  // -----------------------
  // PhotoSwipeLightbox 
  // -----------------------
  initPhotoSwipe() {
    if (typeof PhotoSwipeLightbox === "undefined" || typeof PhotoSwipe === "undefined") return;

    const selector = `#${this.galleryId}`;
    if (!this.querySelector(selector)) return;

    this.lightbox = new PhotoSwipeLightbox({
      gallery: selector,
      children: "a",
      pswpModule: PhotoSwipe,
      bgOpacity: 1,
      secondaryZoomLevel: 2,
      maxZoomLevel: 3,
    });

    this.lightbox.init();

    this.lightbox.on("change", () => {
      const { pswp } = this.lightbox;
      this.main?.slideTo(pswp.currIndex, 0, false);
    });

    this.lightbox.on("afterInit", () => {
      if (this.main?.params?.autoplay?.enabled) this.main.autoplay.stop();
    });

    this.lightbox.on("closingAnimationStart", () => {
      const { pswp } = this.lightbox;
      this.main?.slideTo(pswp.currIndex, 0, false);
      if (this.main?.params?.autoplay?.enabled) this.main.autoplay.start();
    });
  }

  destroyPhotoSwipe() {
    try {
      this.lightbox?.destroy();
    } catch (_) {}
    this.lightbox = null;
  }

  // -----------------------
  // Model Viewer UI (NO jQuery)
  // -----------------------
  initModelViewerUI() {
    const wrappers = this.querySelectorAll(".tf-model-viewer");
    if (!wrappers.length) return;

    wrappers.forEach((wrapper) => {
      const btn = wrapper.querySelector(".tf-model-viewer-ui-button");
      const ui = wrapper.querySelector(".tf-model-viewer-ui");
      const mv = wrapper.querySelector("model-viewer");

      if (btn && mv) {
        btn.addEventListener("click", () => {
          mv.classList.remove("disabled");
          wrapper.classList.toggle("active");
        });
      }

      if (ui && mv) {
        ui.addEventListener("dblclick", () => {
          mv.classList.add("disabled");
          wrapper.classList.toggle("active");

          mv.cameraOrbit = "0deg 90deg auto";
          mv.updateFraming?.();
        });
      }
    });
  }
}

customElements.define("product-gallery-swiper", ProductGallerySwiper);
